// File: apps/web/src/features/import/InFlightImportBanner.tsx
// Version: 2026.4.9 — Persistent in-flight import banner
//
// Renders at the top of the Import page whenever a reservation row exists
// for the current tenant. NOT dismissable — the only ways to make it go
// away are:
//
//   • The job completes (SignalR → ImportPage refreshes hook → null)
//   • User clicks "Reconnect & watch" (banner stays, progress UI mounts
//     below)
//   • User clicks "Cancel" twice (confirmation pattern, no modal)
//
// Style notes:
//   • Uses ENGINE_COLORS tokens like the rest of the import feature, no
//     CSS modules — this codebase is inline-styles + tokens.
//   • Cyan accent matches the Import page's accent color (set on the
//     EnginePageShell). Cancel button uses coral on hover for danger.
//   • CSS-only animations: slide-in on mount, staggered fade for the
//     children. No motion library dep.

import { useState } from 'react';
import { ENGINE_COLORS as C } from '../../components/engine/tokens';
import type { InFlightImportJob, ImportCancelOutcome } from '../../lib/api/types-import';

interface InFlightImportBannerProps {
  job:        InFlightImportJob;
  onReconnect: (batchId: string) => void;
  onCancel:    () => Promise<ImportCancelOutcome>;
}

const STATE_LABELS: Record<InFlightImportJob['state'], string> = {
  Reserved:    'Preparing…',
  Staging:     'Reading conversations',
  Classifying: 'Classifying with AI',
};

export function InFlightImportBanner({
  job,
  onReconnect,
  onCancel,
}: InFlightImportBannerProps) {
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancelClick = () => {
    if (!confirming) {
      setConfirming(true);
      // Auto-disarm after 5 seconds — don't leave a destructive button armed.
      window.setTimeout(() => setConfirming(false), 5000);
      return;
    }
    setCancelling(true);
    void onCancel().finally(() => {
      setCancelling(false);
      setConfirming(false);
    });
  };

  return (
    <>
      <style>{bannerKeyframes}</style>
      <div style={bannerStyle} role="status" aria-live="polite">
        <div style={iconStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.25">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div style={copyStyle}>
          <div style={titleStyle}>An import is running for your organization</div>
          <div style={metaStyle}>
            <span style={stateChipStyle}>{STATE_LABELS[job.state] ?? job.state}</span>
            <span style={dotStyle}>·</span>
            <span style={batchIdStyle} title={job.batchId}>{job.batchId}</span>
          </div>
        </div>

        <div style={actionsStyle}>
          <button
            type="button"
            style={reconnectButtonStyle}
            onClick={() => onReconnect(job.batchId)}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${C.cyan}22`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = `${C.cyan}11`; }}
          >
            Reconnect &amp; watch
          </button>

          <button
            type="button"
            style={confirming ? cancelButtonArmedStyle : cancelButtonStyle}
            onClick={handleCancelClick}
            disabled={cancelling}
          >
            {cancelling
              ? 'Cancelling…'
              : confirming
                ? 'Click again to confirm'
                : 'Cancel import'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const bannerKeyframes = `
@keyframes inflight-slide-in {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes inflight-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes inflight-pulse {
  0%, 100% { box-shadow: 0 0 0 0 ${C.coral}66; }
  50%      { box-shadow: 0 0 0 6px ${C.coral}00; }
}
@media (prefers-reduced-motion: reduce) {
  [data-inflight-banner] { animation: none !important; }
  [data-inflight-banner] * { animation: none !important; }
}
`;

const bannerStyle: React.CSSProperties = {
  display:        'flex',
  alignItems:     'center',
  gap:            16,
  padding:        '14px 18px',
  background:     `linear-gradient(135deg, ${C.cyan}11, ${C.cyan}05)`,
  border:         `1px solid ${C.cyan}44`,
  borderLeft:     `4px solid ${C.cyan}`,
  borderRadius:   8,
  marginBottom:   16,
  color:          '#e5e7eb',
  fontFamily:     '"Inter Display", system-ui, sans-serif',
  animation:      'inflight-slide-in 280ms cubic-bezier(0.16, 1, 0.3, 1)',
  // @ts-expect-error data attr for the keyframes selector
  'data-inflight-banner': 'true',
};

const iconStyle: React.CSSProperties = {
  flexShrink: 0,
  color:      C.cyan,
  display:    'flex',
  alignItems: 'center',
  animation:  'inflight-fade-in 400ms 120ms cubic-bezier(0.16, 1, 0.3, 1) backwards',
};

const copyStyle: React.CSSProperties = {
  flex:      1,
  minWidth:  0,
  animation: 'inflight-fade-in 400ms 180ms cubic-bezier(0.16, 1, 0.3, 1) backwards',
};

const titleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize:   14.5,
  lineHeight: 1.4,
  marginBottom: 3,
  color:      '#f3f4f6',
};

const metaStyle: React.CSSProperties = {
  display:    'flex',
  alignItems: 'center',
  gap:        8,
  fontSize:   12,
  color:      '#9ca3af',
};

const stateChipStyle: React.CSSProperties = {
  fontWeight:    500,
  color:         C.cyan,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontSize:      11,
};

const dotStyle: React.CSSProperties = {
  opacity: 0.5,
};

const batchIdStyle: React.CSSProperties = {
  fontFamily:    '"JetBrains Mono", "SF Mono", Menlo, monospace',
  fontSize:      11,
  letterSpacing: '-0.01em',
  opacity:       0.7,
  maxWidth:      '32ch',
  overflow:      'hidden',
  textOverflow:  'ellipsis',
  whiteSpace:    'nowrap',
};

const actionsStyle: React.CSSProperties = {
  display:    'flex',
  alignItems: 'center',
  gap:        8,
  flexShrink: 0,
  animation:  'inflight-fade-in 400ms 240ms cubic-bezier(0.16, 1, 0.3, 1) backwards',
};

const reconnectButtonStyle: React.CSSProperties = {
  padding:      '8px 16px',
  background:   `${C.cyan}11`,
  color:        C.cyan,
  border:       `1px solid ${C.cyan}66`,
  borderRadius: 6,
  fontFamily:   'inherit',
  fontWeight:   600,
  fontSize:     12.5,
  cursor:       'pointer',
  transition:   'background 160ms ease',
};

const cancelButtonStyle: React.CSSProperties = {
  padding:      '8px 14px',
  background:   'transparent',
  color:        '#9ca3af',
  border:       '1px solid #4b5563',
  borderRadius: 6,
  fontFamily:   'inherit',
  fontWeight:   500,
  fontSize:     12.5,
  cursor:       'pointer',
  transition:   'all 160ms ease',
};

const cancelButtonArmedStyle: React.CSSProperties = {
  ...cancelButtonStyle,
  background:   C.coral,
  color:        '#fff',
  borderColor:  C.coral,
  animation:    'inflight-pulse 1.2s ease-in-out infinite',
};
